import { useCallback, useEffect, useMemo, useState } from "react";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import {
  createSurvey,
  deleteSurvey,
  getSurveyResults,
  listSurveys,
  setSurveyStatus,
  submitSurvey,
  updateSurvey,
} from "../../api/surveys";
import { Modal } from "../../components/modal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const OTHER_RESPONSE_ID = "additional-written-response";
const OTHER_RESPONSE_LABEL = "Is there anything else you would like to share?";

const emptyForm = {
  title: "",
  description: "",
  cadence: "MONTHLY",
  isAnonymous: true,
  allowOtherResponse: false,
  opensAt: "",
  closesAt: "",
  questions: [{ text: "", type: "RATING", required: true, options: [] }],
};
const message = (e, fallback) =>
  Array.isArray(e?.message) ? e.message.join(", ") : e?.message || fallback;
const nice = (value) =>
  `${value || ""}`
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
const date = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No limit";

export default function Surveys() {
  const { companyAccount, currentEmployee, loading: accountLoading } = useCurrentEmployee();
  const isAdmin = Boolean(companyAccount || currentEmployee?.modulePermissions?.includes("surveys"));
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [responseError, setResponseError] = useState("");
  const [editingSurveyId, setEditingSurveyId] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await listSurveys();
      setSurveys(data.surveys || []);
    } catch (e) {
      setError(message(e, "Unable to load surveys."));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!accountLoading) void load();
  }, [accountLoading, load]);
  const counts = useMemo(
    () => ({
      total: surveys.length,
      published: surveys.filter((x) => x.status === "PUBLISHED").length,
      responses: surveys.reduce((n, x) => n + (x.responseCount || 0), 0),
    }),
    [surveys],
  );

  const updateQuestion = (index, key, value) =>
    setForm((old) => ({
      ...old,
      questions: old.questions.map((q, i) =>
        i === index
          ? {
              ...q,
              [key]: value,
              ...(key === "type" &&
              !["SINGLE_CHOICE", "MULTIPLE_CHOICE"].includes(value)
                ? { options: [] }
                : {}),
            }
          : q,
      ),
    }));
  const saveNew = async (event) => {
    event.preventDefault();
    const incompleteChoice = form.questions.find(
      (q) =>
        ["SINGLE_CHOICE", "MULTIPLE_CHOICE"].includes(q.type) &&
        q.options.filter(Boolean).length < 2,
    );
    if (incompleteChoice) {
      setCreateError(
        "Choice questions require at least two comma-separated options.",
      );
      return;
    }
    if (form.allowOtherResponse && form.questions.length >= 30) {
      setCreateError(
        "A survey with an additional written response can have up to 29 created questions.",
      );
      return;
    }
    setSaving(true);
    setError("");
    setCreateError("");
    try {
      const payload = {
        ...form,
        opensAt: form.opensAt || undefined,
        closesAt: form.closesAt || undefined,
        questions: [
          ...form.questions.map((q) => ({
            id: q.id,
            label: q.text.trim(),
            type: q.type,
            required: q.required,
            options: q.options.filter(Boolean),
          })),
          ...(form.allowOtherResponse
            ? [
                {
                  id: OTHER_RESPONSE_ID,
                  label: OTHER_RESPONSE_LABEL,
                  type: "TEXT",
                  required: false,
                  options: [],
                },
              ]
            : []),
        ],
      };
      if (editingSurveyId) await updateSurvey(editingSurveyId, payload);
      else await createSurvey(payload);
      setCreating(false);
      setEditingSurveyId("");
      setForm(emptyForm);
      setNotice(
        editingSurveyId ? "Survey draft updated." : "Survey saved as a draft.",
      );
      await load();
    } catch (e) {
      setCreateError(message(e, "Unable to create survey."));
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (id, status) => {
    try {
      setSaving(true);
      await setSurveyStatus(id, status);
      setNotice(
        status === "PUBLISHED"
          ? "Survey published and employees notified."
          : "Survey closed.",
      );
      await load();
    } catch (e) {
      setError(message(e, "Unable to update survey."));
    } finally {
      setSaving(false);
    }
  };
  const send = async (event) => {
    event.preventDefault();
    const missing = active.questions.find(
      (q) =>
        q.required &&
        (answers[q.id] === undefined ||
          answers[q.id] === "" ||
          (Array.isArray(answers[q.id]) && !answers[q.id].length)),
    );
    if (missing) {
      setResponseError(
        `Please answer: ${missing.label || missing.text || "required question"}`,
      );
      return;
    }
    try {
      setSaving(true);
      setResponseError("");
      await submitSurvey(
        active.id,
        Object.entries(answers).map(([questionId, value]) => ({
          questionId,
          value,
        })),
      );
      setActive(null);
      setAnswers({});
      setNotice("Thank you. Your response has been recorded.");
      await load();
    } catch (e) {
      setResponseError(message(e, "Unable to submit response."));
    } finally {
      setSaving(false);
    }
  };
  const showResults = async (survey) => {
    try {
      setSaving(true);
      setResults(await getSurveyResults(survey.id));
    } catch (e) {
      setError(message(e, "Unable to load results."));
    } finally {
      setSaving(false);
    }
  };
  const editDraft = (survey) => {
    const surveyQuestions = survey.questions || [];
    setEditingSurveyId(survey.id);
    setCreateError("");
    setForm({
      title: survey.title || "",
      description: survey.description || "",
      cadence: survey.cadence || "MONTHLY",
      isAnonymous: survey.isAnonymous !== false,
      allowOtherResponse: surveyQuestions.some(
        (q) => q.id === OTHER_RESPONSE_ID,
      ),
      opensAt: survey.opensAt
        ? new Date(survey.opensAt).toISOString().slice(0, 16)
        : "",
      closesAt: survey.closesAt
        ? new Date(survey.closesAt).toISOString().slice(0, 16)
        : "",
      questions: surveyQuestions
        .filter((q) => q.id !== OTHER_RESPONSE_ID)
        .map((q) => ({
          id: q.id,
          text: q.label || q.text || "",
          type: q.type,
          required: q.required !== false,
          options: q.options || [],
        })),
    });
    setCreating(true);
  };
  const removeDraft = async (survey) => {
    if (!window.confirm(`Delete the draft “${survey.title}”?`)) return;
    try {
      setSaving(true);
      await deleteSurvey(survey.id);
      setNotice("Survey draft deleted.");
      await load();
    } catch (e) {
      setError(message(e, "Unable to delete survey."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4 font-Nunito sm:p-6">
      {creating && createError ? (
        <div className="fixed left-1/2 top-4 z-[60] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 rounded-lg bg-red-600 p-3 text-sm font-semibold text-white shadow-lg">
          {createError}
        </div>
      ) : null}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Employee Surveys</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin
              ? "Create engagement surveys and turn feedback into action."
              : "Share your feedback securely with HR."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setEditingSurveyId(""); setForm(emptyForm); setCreateError(""); setCreating(true); }}
            className="rounded-lg bg-[#1234CD] px-5 py-3 font-semibold text-white"
          >
            Create survey
          </button>
        )}
      </div>
      {notice && (
        <div className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {isAdmin && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          {[
            ["All surveys", counts.total],
            ["Currently live", counts.published],
            ["Responses", counts.responses],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border bg-white p-5">
              <p className="text-sm text-slate-500">{label}</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{value}</p>
            </div>
          ))}
        </div>
      )}
      {loading ? (
        <FetchLoadingState message="Loading surveys..." className="min-h-[55vh] rounded-xl bg-white" />
      ) : surveys.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center">
          <h2 className="font-semibold">No surveys available</h2>
          <p className="mt-1 text-sm text-slate-500">
            {isAdmin
              ? "Create the first monthly or quarterly survey."
              : "New surveys will appear here when HR publishes them."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {surveys.map((survey) => (
            <article
              key={survey.id}
              className="rounded-xl border bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                      {nice(survey.cadence)}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${survey.status === "PUBLISHED" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {nice(survey.status)}
                    </span>
                    {survey.questions?.some(
                      (q) => q.id === OTHER_RESPONSE_ID,
                    ) && (
                      <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                        Written feedback enabled
                      </span>
                    )}
                  </div>
                  <h2 className="mt-3 text-lg font-bold">{survey.title}</h2>
                </div>
                <span className="text-xs text-slate-500">
                  {survey.isAnonymous ? "Anonymous" : "Identified"}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {survey.description || "Employee feedback survey"}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                <span>
                  {(survey.questions || []).filter(
                    (q) => q.id !== OTHER_RESPONSE_ID,
                  ).length} questions
                </span>
                <span>Opens {date(survey.opensAt)}</span>
                <span>Closes {date(survey.closesAt)}</span>
                {isAdmin && <span>{survey.responseCount || 0} responses</span>}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {isAdmin ? (
                  <>
                    <button
                      onClick={() => showResults(survey)}
                      className="rounded-lg border px-4 py-2 text-sm font-semibold"
                    >
                      View results
                    </button>
                    {survey.status === "DRAFT" && (
                      <>
                        <button disabled={saving} onClick={() => editDraft(survey)} className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-700">Edit</button>
                        <button disabled={saving} onClick={() => removeDraft(survey)} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700">Delete</button>
                      </>
                    )}
                    {survey.status !== "PUBLISHED" && (
                      <button
                        disabled={saving}
                        onClick={() => changeStatus(survey.id, "PUBLISHED")}
                        className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white"
                      >
                        Publish
                      </button>
                    )}
                    {survey.status === "PUBLISHED" && (
                      <button
                        disabled={saving}
                        onClick={() => changeStatus(survey.id, "CLOSED")}
                        className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white"
                      >
                        Close
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    disabled={survey.hasResponded}
                    onClick={() => {
                      setActive(survey);
                      setAnswers({});
                    }}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold ${survey.hasResponded ? "bg-emerald-50 text-emerald-700" : "bg-[#1234CD] text-white"}`}
                  >
                    {survey.hasResponded ? "Response submitted" : "Take survey"}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        isOpen={creating}
        onClose={() => {
          setCreating(false);
          setCreateError("");
        }}
      >
        <form onSubmit={saveNew}>
          <h2 className="text-xl font-bold">{editingSurveyId ? "Edit survey draft" : "Create employee survey"}</h2>
          <div className="mt-4 space-y-3">
            {createError ? (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {createError}
              </div>
            ) : null}
            <input
              required
              placeholder="Survey title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-lg border p-3"
            />
            <textarea
              placeholder="Purpose or introduction"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="w-full rounded-lg border p-3"
            />
            <div className="grid grid-cols-2 gap-3">
              <select
                value={form.cadence}
                onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                className="rounded-lg border p-3"
              >
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ONE_TIME">One time</option>
              </select>
              <label className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={form.isAnonymous}
                  onChange={(e) =>
                    setForm({ ...form, isAnonymous: e.target.checked })
                  }
                />{" "}
                Anonymous
              </label>
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 transition hover:border-blue-300">
              <input
                type="checkbox"
                checked={form.allowOtherResponse}
                onChange={(e) =>
                  setForm({
                    ...form,
                    allowOtherResponse: e.target.checked,
                  })
                }
                className="mt-1 h-4 w-4 accent-[#1234CD]"
              />
              <span>
                <span className="block text-sm font-bold text-slate-800">
                  Allow an additional written response
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-500">
                  Respondents can optionally type other feedback that is not
                  covered by the created questions or ratings.
                </span>
              </span>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">
                Opening date and time
                <input
                  type="datetime-local"
                  value={form.opensAt}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  onChange={(e) =>
                    setForm({ ...form, opensAt: e.target.value })
                  }
                  className="mt-1 w-full cursor-pointer rounded-lg border p-3 font-normal"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Closing date and time
                <input
                  type="datetime-local"
                  value={form.closesAt}
                  min={form.opensAt || undefined}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  onChange={(e) =>
                    setForm({ ...form, closesAt: e.target.value })
                  }
                  className="mt-1 w-full cursor-pointer rounded-lg border p-3 font-normal"
                />
              </label>
            </div>
            {form.questions.map((q, i) => (
              <div key={i} className="rounded-lg bg-slate-50 p-3">
                <input
                  required
                  placeholder={`Question ${i + 1}`}
                  value={q.text}
                  onChange={(e) => updateQuestion(i, "text", e.target.value)}
                  className="w-full rounded-lg border p-2"
                />
                <div className="mt-2 flex gap-2">
                  <select
                    value={q.type}
                    onChange={(e) => updateQuestion(i, "type", e.target.value)}
                    className="flex-1 rounded-lg border p-2"
                  >
                    <option value="RATING">Rating (1–5)</option>
                    <option value="SINGLE_CHOICE">Single choice</option>
                    <option value="MULTIPLE_CHOICE">Multiple choice</option>
                    <option value="TEXT">Text</option>
                  </select>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={(e) =>
                        updateQuestion(i, "required", e.target.checked)
                      }
                    />{" "}
                    Required
                  </label>
                </div>
                {["SINGLE_CHOICE", "MULTIPLE_CHOICE"].includes(q.type) && (
                  <input
                    value={(q.options || []).join(", ")}
                    placeholder="Options separated by commas"
                    onChange={(e) =>
                      updateQuestion(
                        i,
                        "options",
                        e.target.value.split(",").map((x) => x.trim()),
                      )
                    }
                    className="mt-2 w-full rounded-lg border p-2"
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  questions: [
                    ...form.questions,
                    { text: "", type: "RATING", required: true, options: [] },
                  ],
                })
              }
              className="text-sm font-semibold text-blue-700"
            >
              + Add question
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-lg bg-[#1234CD] p-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving…" : editingSurveyId ? "Update draft" : "Save draft"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(active)} onClose={() => { setActive(null); setResponseError(""); }}>
        {active && (
          <form onSubmit={send}>
            <h2 className="text-xl font-bold">{active.title}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {active.isAnonymous
                ? "Your identity will not be shown in results."
                : "Your response is identifiable."}
            </p>
            <div className="mt-5 space-y-5">
              {responseError ? <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{responseError}</div> : null}
              {active.questions.map((q) => (
                <label key={q.id} className="block">
                  <span className="mb-2 block text-sm font-semibold">
                    {q.label || q.text || "Untitled question"}
                    {q.required && " *"}
                  </span>
                  {q.type === "TEXT" && (
                    <textarea
                      required={q.required}
                      maxLength={2000}
                      rows={q.id === OTHER_RESPONSE_ID ? 4 : 3}
                      placeholder={
                        q.id === OTHER_RESPONSE_ID
                          ? "Type any additional comments here (optional)"
                          : "Type your response"
                      }
                      onChange={(e) =>
                        setAnswers({ ...answers, [q.id]: e.target.value })
                      }
                      className="w-full rounded-lg border p-3"
                    />
                  )}
                  {q.type === "RATING" && (
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setAnswers({ ...answers, [q.id]: n })}
                          className={`h-10 w-10 rounded-full border ${answers[q.id] === n ? "bg-blue-600 text-white" : "bg-white"}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  {q.type === "SINGLE_CHOICE" &&
                    q.options.map((o) => (
                      <label key={o} className="mr-4 inline-flex gap-2 text-sm">
                        <input
                          type="radio"
                          name={q.id}
                          required={q.required}
                          onChange={() => setAnswers({ ...answers, [q.id]: o })}
                        />
                        {o}
                      </label>
                    ))}
                  {q.type === "MULTIPLE_CHOICE" &&
                    q.options.map((o) => (
                      <label key={o} className="mr-4 inline-flex gap-2 text-sm">
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            setAnswers({
                              ...answers,
                              [q.id]: e.target.checked
                                ? [...(answers[q.id] || []), o]
                                : (answers[q.id] || []).filter((x) => x !== o),
                            })
                          }
                        />
                        {o}
                      </label>
                    ))}
                </label>
              ))}
              <button
                type="submit"
                disabled={saving}
                className="w-full rounded-lg bg-[#1234CD] p-3 font-semibold text-white"
              >
                {saving ? "Submitting…" : "Submit response"}
              </button>
            </div>
          </form>
        )}
      </Modal>
      <Modal isOpen={Boolean(results)} onClose={() => setResults(null)}>
        {results && (
          <div>
            <h2 className="text-xl font-bold">{results.survey.title}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {results.responseCount} responses · Aggregated results
            </p>
            <div className="mt-5 space-y-4">
              {results.results.map((r) => (
                <div key={r.questionId} className="rounded-lg bg-slate-50 p-4">
                  <p className="font-semibold">{r.label || r.text || "Untitled question"}</p>
                  {r.average != null && (
                    <p className="mt-2 text-2xl font-bold text-blue-700">
                      {r.average.toFixed(1)} / 5
                    </p>
                  )}
                  <div className="mt-2 space-y-1 text-sm">
                    {Object.entries(r.counts || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span>{key}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!results.survey.isAnonymous && results.identifiedResponses?.length ? (
              <div className="mt-6 border-t pt-5">
                <h3 className="font-bold text-slate-900">Identified responses</h3>
                <div className="mt-3 space-y-3">
                  {results.identifiedResponses.map((response) => (
                    <details key={response.id} className="rounded-lg border p-3">
                      <summary className="cursor-pointer font-semibold text-slate-700">{response.employee.fullName} · {new Date(response.submittedAt).toLocaleString()}</summary>
                      <div className="mt-3 space-y-2 text-sm">
                        {response.answers.map((answer) => {
                          const question = results.results.find((item) => item.questionId === answer.questionId);
                          return <div key={answer.questionId}><span className="font-semibold">{question?.label || "Question"}: </span><span>{Array.isArray(answer.value) ? answer.value.join(", ") : `${answer.value ?? ""}`}</span></div>;
                        })}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
